import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ExternalLink, Loader2, RefreshCw, AlertTriangle } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EmailSendRow {
  message_id: string;
  template_name: string;
  recipient_email: string | null;
  status: string; // pending | sent | dlq | suppressed | failed | bounced | complained
  error_message: string | null;
  created_at: string;
  metadata: Record<string, unknown> | null;
}

interface PaymentRow {
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_customer_id: string | null;
  status: string;
  amount_total: number | null;
  currency: string | null;
  environment: string | null;
}

interface Attempt {
  messageId: string;
  sessionId: string | null;
  eventType: string | null;
  recipient: string | null;
  status: string;
  errorMessage: string | null;
  createdAt: string;
  attempts: number;
  suppressed: boolean;
  suppressionReason: string | null;
  payment: PaymentRow | null;
  opens: number;
  clicks: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TEMPLATE_NAME = "abandoned-checkout";

// message_id format from payments-webhook: `abandoned-checkout:${sessionId}:${eventType}`
function parseMessageId(id: string): { sessionId: string | null; eventType: string | null } {
  const parts = id.split(":");
  if (parts.length < 3 || parts[0] !== TEMPLATE_NAME) {
    return { sessionId: null, eventType: null };
  }
  return { sessionId: parts[1] || null, eventType: parts.slice(2).join(":") || null };
}

function stripeSessionUrl(sessionId: string | null, environment?: string | null) {
  if (!sessionId) return null;
  const prefix = environment === "live" ? "" : "test/";
  return `https://dashboard.stripe.com/${prefix}payments?query=${encodeURIComponent(sessionId)}`;
}

function stripePaymentIntentUrl(piId: string | null, environment?: string | null) {
  if (!piId) return null;
  const prefix = environment === "live" ? "" : "test/";
  return `https://dashboard.stripe.com/${prefix}payments/${encodeURIComponent(piId)}`;
}

function stripeCustomerUrl(customerId: string | null, environment?: string | null) {
  if (!customerId) return null;
  const prefix = environment === "live" ? "" : "test/";
  return `https://dashboard.stripe.com/${prefix}customers/${encodeURIComponent(customerId)}`;
}

function formatMoney(amount: number | null, currency: string | null) {
  if (amount == null || !currency) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  } catch {
    return `${amount / 100} ${currency.toUpperCase()}`;
  }
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "sent":
      return "default";
    case "pending":
      return "secondary";
    case "suppressed":
    case "bounced":
    case "complained":
      return "outline";
    case "dlq":
    case "failed":
      return "destructive";
    default:
      return "secondary";
  }
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

interface LoadResult {
  attempts: Attempt[];
  emailInfraMissing: boolean;
  loadError: string | null;
}

// Postgres error codes we treat as "table not created yet".
const MISSING_RELATION_CODES = new Set(["42P01", "PGRST205"]);

async function loadAttempts(limit: number): Promise<LoadResult> {
  // 1. Latest send log rows for the abandoned-checkout template.
  const { data: sendRows, error: sendErr } = await supabase
    .from("email_send_log" as never)
    .select("message_id, template_name, recipient_email, status, error_message, created_at, metadata")
    .eq("template_name", TEMPLATE_NAME)
    .order("created_at", { ascending: false })
    .limit(limit * 5); // over-fetch so grouping still yields ~limit unique messages

  if (sendErr) {
    if (MISSING_RELATION_CODES.has(sendErr.code ?? "")) {
      return { attempts: [], emailInfraMissing: true, loadError: null };
    }
    return { attempts: [], emailInfraMissing: false, loadError: sendErr.message };
  }

  const rows = (sendRows ?? []) as unknown as EmailSendRow[];

  // Dedup by message_id keeping the newest row + counting attempts.
  const byMessage = new Map<
    string,
    { latest: EmailSendRow; attempts: number }
  >();
  for (const row of rows) {
    const key = row.message_id;
    const existing = byMessage.get(key);
    if (!existing) {
      byMessage.set(key, { latest: row, attempts: 1 });
    } else {
      existing.attempts += 1;
      if (new Date(row.created_at) > new Date(existing.latest.created_at)) {
        existing.latest = row;
      }
    }
  }

  const grouped = Array.from(byMessage.values())
    .sort((a, b) => new Date(b.latest.created_at).getTime() - new Date(a.latest.created_at).getTime())
    .slice(0, limit);

  const sessionIds = Array.from(
    new Set(
      grouped
        .map((g) => parseMessageId(g.latest.message_id).sessionId)
        .filter((v): v is string => !!v),
    ),
  );
  const recipients = Array.from(
    new Set(
      grouped
        .map((g) => g.latest.recipient_email?.toLowerCase())
        .filter((v): v is string => !!v),
    ),
  );

  // 2. Payments (Stripe session + PI + customer) for those sessions.
  let paymentsBySession = new Map<string, PaymentRow>();
  if (sessionIds.length) {
    const { data: payments } = await supabase
      .from("payments")
      .select(
        "stripe_session_id, stripe_payment_intent_id, stripe_customer_id, status, amount_total, currency, environment",
      )
      .in("stripe_session_id", sessionIds);
    for (const p of (payments ?? []) as PaymentRow[]) {
      if (p.stripe_session_id) paymentsBySession.set(p.stripe_session_id, p);
    }
  }

  // 3. Suppression rows for those recipients.
  const suppressedByEmail = new Map<string, string | null>();
  if (recipients.length) {
    const { data: suppressed, error: suppErr } = await supabase
      .from("suppressed_emails" as never)
      .select("email, reason")
      .in("email", recipients);
    if (!suppErr) {
      for (const s of (suppressed ?? []) as unknown as { email: string; reason: string | null }[]) {
        suppressedByEmail.set(s.email.toLowerCase(), s.reason ?? null);
      }
    }
    // Missing suppressed_emails table is non-fatal — leave map empty.
  }

  // 4. Opens + clicks counts per original abandoned session id.
  const opensBySession = new Map<string, number>();
  const clicksBySession = new Map<string, number>();
  if (sessionIds.length) {
    const [opensRes, clicksRes] = await Promise.all([
      supabase.from("email_opens").select("session_id").in("session_id", sessionIds),
      supabase.from("email_retry_clicks").select("session_id").in("session_id", sessionIds),
    ]);
    for (const r of (opensRes.data ?? []) as { session_id: string | null }[]) {
      if (r.session_id) opensBySession.set(r.session_id, (opensBySession.get(r.session_id) ?? 0) + 1);
    }
    for (const r of (clicksRes.data ?? []) as { session_id: string | null }[]) {
      if (r.session_id) clicksBySession.set(r.session_id, (clicksBySession.get(r.session_id) ?? 0) + 1);
    }
  }

  const attempts: Attempt[] = grouped.map(({ latest, attempts: count }) => {
    const { sessionId, eventType } = parseMessageId(latest.message_id);
    const recipient = latest.recipient_email?.toLowerCase() ?? null;
    return {
      messageId: latest.message_id,
      sessionId,
      eventType,
      recipient: latest.recipient_email,
      status: latest.status,
      errorMessage: latest.error_message,
      createdAt: latest.created_at,
      attempts: count,
      suppressed: recipient ? suppressedByEmail.has(recipient) : false,
      suppressionReason: recipient ? suppressedByEmail.get(recipient) ?? null : null,
      payment: sessionId ? paymentsBySession.get(sessionId) ?? null : null,
      opens: sessionId ? opensBySession.get(sessionId) ?? 0 : 0,
      clicks: sessionId ? clicksBySession.get(sessionId) ?? 0 : 0,
    };
  });

  return { attempts, emailInfraMissing: false, loadError: null };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  "all",
  "sent",
  "pending",
  "dlq",
  "failed",
  "suppressed",
  "bounced",
  "complained",
] as const;

export default function AdminAbandonedEmails() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [emailInfraMissing, setEmailInfraMissing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [limit, setLimit] = useState(100);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_OPTIONS)[number]>("all");
  const [search, setSearch] = useState("");

  // Admin gate — RLS also enforces server-side.
  useEffect(() => {
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user.id;
      if (!uid) {
        setIsAdmin(false);
        return;
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    })();
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    const res = await loadAttempts(limit);
    setAttempts(res.attempts);
    setEmailInfraMissing(res.emailInfraMissing);
    setLoadError(res.loadError);
    setRefreshing(false);
    setLoading(false);
  }, [limit]);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return attempts.filter((a) => {
      if (statusFilter === "suppressed") {
        if (!a.suppressed && a.status !== "suppressed") return false;
      } else if (statusFilter !== "all") {
        if (a.status !== statusFilter) return false;
      }
      if (!q) return true;
      return (
        (a.recipient?.toLowerCase().includes(q) ?? false) ||
        (a.sessionId?.toLowerCase().includes(q) ?? false) ||
        (a.payment?.stripe_payment_intent_id?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [attempts, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { total: attempts.length, sent: 0, failed: 0, suppressed: 0, pending: 0 };
    for (const a of attempts) {
      if (a.status === "sent") c.sent++;
      else if (a.status === "dlq" || a.status === "failed") c.failed++;
      else if (a.status === "pending") c.pending++;
      if (a.suppressed || a.status === "suppressed") c.suppressed++;
    }
    return c;
  }, [attempts]);

  if (isAdmin === false) {
    return (
      <main className="container py-16">
        <Helmet>
          <title>Admin — Abandoned Checkout Emails</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Alert variant="destructive">
          <AlertTitle>Admins only</AlertTitle>
          <AlertDescription>
            You need the <code>admin</code> role to view this page.
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <main className="container py-8 space-y-6">
      <Helmet>
        <title>Admin — Abandoned Checkout Emails</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Abandoned Checkout Emails</h1>
          <p className="text-sm text-muted-foreground">
            Delivery + suppression status per recovery-email attempt, with Stripe session and payment
            links.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}>
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </header>

      {emailInfraMissing && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Email delivery logging isn't set up yet</AlertTitle>
          <AlertDescription>
            Delivery status comes from the email send log, which is created when email
            infrastructure is provisioned. Set up a sender domain in Cloud → Emails and
            delivery data will start appearing here automatically.
          </AlertDescription>
        </Alert>
      )}

      {loadError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load attempts</AlertTitle>
          <AlertDescription className="font-mono text-xs">{loadError}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Attempts" value={counts.total} />
        <StatCard label="Sent" value={counts.sent} tone="success" />
        <StatCard label="Pending" value={counts.pending} />
        <StatCard label="Failed" value={counts.failed} tone="destructive" />
        <StatCard label="Suppressed" value={counts.suppressed} tone="warning" />
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base">Recent attempts</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search email, session, or PI…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64"
            />
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="h-9 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s === "all" ? "All statuses" : s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
              <SelectTrigger className="h-9 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[50, 100, 200, 500].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No abandoned-checkout email attempts yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Suppression</TableHead>
                    <TableHead>Opens / Clicks</TableHead>
                    <TableHead>Stripe session</TableHead>
                    <TableHead>Payment intent</TableHead>
                    <TableHead>Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((a) => (
                    <AttemptRow key={a.messageId} attempt={a} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "success" | "destructive" | "warning";
}) {
  const toneClass =
    tone === "success"
      ? "text-green-600 dark:text-green-400"
      : tone === "destructive"
        ? "text-destructive"
        : tone === "warning"
          ? "text-amber-600 dark:text-amber-400"
          : "";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function AttemptRow({ attempt }: { attempt: Attempt }) {
  const env = attempt.payment?.environment ?? null;
  const sessionUrl = stripeSessionUrl(attempt.sessionId, env);
  const piUrl = stripePaymentIntentUrl(attempt.payment?.stripe_payment_intent_id ?? null, env);
  const customerUrl = stripeCustomerUrl(attempt.payment?.stripe_customer_id ?? null, env);

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-xs">
        {new Date(attempt.createdAt).toLocaleString()}
        {attempt.attempts > 1 && (
          <div className="text-[10px] text-muted-foreground">{attempt.attempts} attempts</div>
        )}
      </TableCell>
      <TableCell className="text-xs">
        <div>{attempt.recipient ?? "—"}</div>
        {customerUrl && (
          <a
            href={customerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:underline"
          >
            {attempt.payment?.stripe_customer_id}
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </TableCell>
      <TableCell className="text-xs font-mono">{attempt.eventType ?? "—"}</TableCell>
      <TableCell>
        <Badge variant={statusVariant(attempt.status)}>{attempt.status}</Badge>
        {attempt.errorMessage && (
          <div
            className="text-[10px] text-destructive mt-1 max-w-[220px] truncate"
            title={attempt.errorMessage}
          >
            {attempt.errorMessage}
          </div>
        )}
      </TableCell>
      <TableCell>
        {attempt.suppressed ? (
          <Badge variant="outline" className="border-amber-500 text-amber-600">
            {attempt.suppressionReason ?? "suppressed"}
          </Badge>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {attempt.opens} / {attempt.clicks}
      </TableCell>
      <TableCell className="text-xs">
        {sessionUrl ? (
          <a
            href={sessionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:underline"
            title={attempt.sessionId ?? undefined}
          >
            {attempt.sessionId?.slice(0, 18)}…
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs">
        {piUrl ? (
          <a
            href={piUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:underline"
          >
            {attempt.payment?.stripe_payment_intent_id}
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {formatMoney(attempt.payment?.amount_total ?? null, attempt.payment?.currency ?? null)}
      </TableCell>
    </TableRow>
  );
}
